import { useAuth } from "@/hooks/use-auth-simple";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lightbulb, ArrowLeft, Sparkles, FileText, Search, Users, BarChart3 } from "lucide-react";
import { Link, Redirect } from "wouter";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useState, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";

type SafeUser = {
  id: string;
  username: string;
  isAdmin?: boolean;
  is_admin?: boolean;
  department?: string;
};

type Project = {
  id: string;
  title: string;
  description: string;
  status: 'planning' | 'proposed' | 'in-development' | 'on-hold';
  priority: 'high' | 'medium' | 'low';
  buildStatus: 'planning' | 'building' | 'needs-review' | 'approval' | 'completed' | 'on-hold';
  category: string;
  icon: React.ReactNode;
  linkTo?: string;
};

// Priority order: high = 3, medium = 2, low = 1
const priorityOrder = {
  high: 3,
  medium: 2,
  low: 1,
};

const statusColors = {
  planning: 'bg-gray-100 text-gray-800 border-gray-200',
  proposed: 'bg-gray-200 text-gray-800 border-gray-300',
  'in-development': 'bg-gray-300 text-gray-900 border-gray-400',
  'on-hold': 'bg-gray-100 text-gray-800 border-gray-200'
};

const statusLabels = {
  planning: 'In Planning',
  proposed: 'Proposed',
  'in-development': 'Currently Being Built',
  'on-hold': 'On Hold'
};

// Sample projects - will be replaced with database later
const sampleProjects: Project[] = [
  {
    id: '1',
    title: 'Guidelines Management System',
    description: 'Section-based editing and chunking system for guidelines. Allows team leads to update guidelines in organized sections, with automatic chunking for AI search integration.',
    status: 'planning',
    priority: 'high',
    buildStatus: 'planning',
    category: 'Guidelines',
    icon: <FileText className="h-6 w-6" />,
    linkTo: '/guidelines'
  },
  {
    id: '2',
    title: 'Enhanced Search Features',
    description: 'Multi-document search with advanced filtering, product-specific search, and improved relevance ranking for better AI responses.',
    status: 'proposed',
    priority: 'medium',
    buildStatus: 'building',
    category: 'Search',
    icon: <Search className="h-6 w-6" />
  },
  {
    id: '3',
    title: 'Real-time Collaboration',
    description: 'Allow multiple team members to collaborate on guideline updates in real-time with version control and change tracking.',
    status: 'proposed',
    priority: 'low',
    buildStatus: 'needs-review',
    category: 'Collaboration',
    icon: <Users className="h-6 w-6" />
  },
  {
    id: '4',
    title: 'Advanced Product Dashboard',
    description: 'Comprehensive analytics for product performance, user feedback, and AI response quality metrics. Currently being built and available in Response Hub.',
    status: 'in-development',
    priority: 'high',
    buildStatus: 'approval',
    category: 'Analytics',
    icon: <BarChart3 className="h-6 w-6" />,
    linkTo: '/response-hub?tab=insights'
  }
];

export default function ProjectDevelopmentPage() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>(sampleProjects);

  // Sort projects by priority (highest first)
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }, [projects]);

  // Update priority mutation
  const updatePriorityMutation = useMutation({
    mutationFn: async ({ projectId, priority }: { projectId: string; priority: 'high' | 'medium' | 'low' }) => {
      // TODO: Replace with actual API call when connected to database
      // const res = await apiRequest('PATCH', `/api/feature-requests/${projectId}`, { priority });
      // return await res.json();
      return { success: true };
    },
    onSuccess: (_, variables) => {
      setProjects(prev => prev.map(project => 
        project.id === variables.projectId 
          ? { ...project, priority: variables.priority }
          : project
      ));
      queryClient.invalidateQueries({ queryKey: ['/api/feature-requests'] });
      toast({
        title: "Priority Updated",
        description: "Project priority has been updated.",
      });
    },
  });

  // Update build status mutation
  const updateBuildStatusMutation = useMutation({
    mutationFn: async ({ projectId, buildStatus }: { projectId: string; buildStatus: Project['buildStatus'] }) => {
      // TODO: Replace with actual API call when connected to database
      // const res = await apiRequest('PATCH', `/api/feature-requests/${projectId}`, { buildStatus });
      // return await res.json();
      return { success: true };
    },
    onSuccess: (_, variables) => {
      setProjects(prev => prev.map(project => 
        project.id === variables.projectId 
          ? { ...project, buildStatus: variables.buildStatus }
          : project
      ));
      queryClient.invalidateQueries({ queryKey: ['/api/feature-requests'] });
      toast({
        title: "Status Updated",
        description: "Build status has been updated.",
      });
    },
  });

  const handlePriorityChange = (projectId: string, priority: 'high' | 'medium' | 'low') => {
    updatePriorityMutation.mutate({ projectId, priority });
  };

  const handleBuildStatusChange = (projectId: string, buildStatus: Project['buildStatus']) => {
    updateBuildStatusMutation.mutate({ projectId, buildStatus });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a1a]"></div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  if (!(user?.isAdmin || (user as any)?.is_admin)) {
    return <Redirect to="/" />;
  }

  return (
    <div 
      className="min-h-screen bg-[#1a1a1a] text-gray-100" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* Header */}
      <header 
        className="px-4 sm:px-6 h-14 border-b border-[#3a3a3a] bg-[#2a2a2a] sticky top-0 z-50"
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <Link href="/admin">
              <Button 
                variant="ghost" 
                size="sm" 
                className="transition-all duration-200 rounded-md px-3 text-gray-300 hover:text-gray-100 hover:bg-[#3a3a3a] border border-[#3a3a3a] touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Back to Admin</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </Link>
            <div className="border-l border-[#3a3a3a] pl-4 h-10 flex items-center">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-100" style={{ textAlign: 'left' }}>
                <Lightbulb className="h-5 w-5 text-yellow-400" />
                <span style={{ textAlign: 'left' }}>Project Development</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" style={{ textAlign: 'left' }}>
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-100 mb-2 px-2 sm:px-0" style={{ textAlign: 'left' }}>
            Projects & Future Features
          </h1>
          <p className="text-sm sm:text-base text-gray-400 px-2 sm:px-0" style={{ textAlign: 'left', lineHeight: '1.6' }}>
            Explore upcoming features and projects in development. These are experimental concepts and may change before release.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-2 sm:px-0">
          {sortedProjects.map((project) => {
            const cardContent = (
              <Card 
                className="rounded-lg border border-[#3a3a3a] bg-[#2a2a2a] shadow-lg hover:shadow-xl transition-all duration-200 hover:border-[#4a4a4a] cursor-pointer group h-full flex flex-col"
                style={{ textAlign: 'left' }}
              >
                <CardHeader className="pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="p-2 rounded-lg bg-[#3a3a3a] text-gray-300 group-hover:bg-[#4a4a4a] transition-colors flex-shrink-0">
                      {project.icon}
                    </div>
                    <Badge 
                      className={`${statusColors[project.status]} border text-xs font-medium flex-shrink-0`}
                    >
                      {statusLabels[project.status]}
                    </Badge>
                  </div>
                  <CardTitle className="text-gray-100 text-base sm:text-lg font-semibold mb-1" style={{ textAlign: 'left' }}>
                    {project.title}
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-xs" style={{ textAlign: 'left' }}>
                    {project.category}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col px-4 sm:px-6 pb-4 sm:pb-6">
                  <p className="text-gray-300 text-sm leading-relaxed mb-4" style={{ textAlign: 'left', lineHeight: '1.6' }}>
                    {project.description}
                  </p>
                  
                  {/* Priority and Status Dropdowns */}
                  <div className="flex flex-col gap-2 mt-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <span className="text-xs text-gray-400 whitespace-nowrap">Priority:</span>
                      <Select 
                        value={project.priority} 
                        onValueChange={(value: 'high' | 'medium' | 'low') => handlePriorityChange(project.id, value)}
                      >
                        <SelectTrigger className="h-9 sm:h-8 w-full bg-[#1a1a1a] border-[#3a3a3a] text-gray-100 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <span className="text-xs text-gray-400 whitespace-nowrap">Status:</span>
                      <Select 
                        value={project.buildStatus} 
                        onValueChange={(value: Project['buildStatus']) => handleBuildStatusChange(project.id, value)}
                      >
                        <SelectTrigger className="h-9 sm:h-8 w-full bg-[#1a1a1a] border-[#3a3a3a] text-gray-100 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
                          <SelectItem value="planning">Planning</SelectItem>
                          <SelectItem value="building">Building</SelectItem>
                          <SelectItem value="needs-review">Needs Review</SelectItem>
                          <SelectItem value="approval">Approval</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="on-hold">On Hold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );

            // If linkTo is provided, link to external page, otherwise link to detail page
            if (project.linkTo) {
              return (
                <Link key={project.id} href={project.linkTo}>
                  {cardContent}
                </Link>
              );
            } else {
              return (
                <Link key={project.id} href={`/project-development/project/${project.id}`}>
                  {cardContent}
                </Link>
              );
            }
          })}
        </div>

        {/* Empty State Message (if no projects) */}
        {sortedProjects.length === 0 && (
          <Card className="rounded-lg border border-[#3a3a3a] bg-[#2a2a2a] p-8 sm:p-12 text-center mx-2 sm:mx-0">
            <Sparkles className="h-10 w-10 sm:h-12 sm:w-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-300 mb-2">No Projects Yet</h3>
            <p className="text-sm sm:text-base text-gray-500">Check back soon for new projects and features.</p>
          </Card>
        )}

        {/* Footer Note */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-[#3a3a3a] px-2 sm:px-0">
          <p className="text-xs sm:text-sm text-gray-500 text-center" style={{ lineHeight: '1.6' }}>
            This page is for demonstration purposes. Features shown here are conceptual and may not be implemented.
          </p>
        </div>
      </main>
    </div>
  );
}
